import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Users, UserCheck, UserX, RefreshCw, Clock, Search, Pencil, ShieldBan, ShieldCheck } from 'lucide-react';
import { getPartnerAnalytics, togglePartnerBlock } from '../../../constant/Constant';
import { HitApi } from '../../../Api/ApiHit';
import { getElevateUser } from '../../../storage/Storage';
import EditPartnerModal from './EditPartnerModal';
import toast from 'react-hot-toast';

const REFRESH_OPTIONS = [
  { label: '5s', value: 5000 },
  { label: '10s', value: 10000 },
  { label: '30s', value: 30000 },
  { label: '1m', value: 60000 },
  { label: '5m', value: 300000 },
];

const FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Online', value: 'online' },
  { label: 'Offline', value: 'offline' },
];

const PartnerAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalPartners: 0,
    onlinePartners: 0,
    offlinePartners: 0
  });
  const [partners, setPartners] = useState({ docs: [], totalPages: 1, page: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [countdown, setCountdown] = useState(5);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Edit modal state
  const [editPartner, setEditPartner] = useState(null);

  const handleToggleBlock = (partner) => {
    const newBlockedState = !partner.isPartnerBlocked;
    const action = newBlockedState ? 'block' : 'unblock';
    if (!window.confirm(`Are you sure you want to ${action} ${partner.name || 'this partner'}?`)) return;

    const user = getElevateUser();
    HitApi({
      _id: partner._id,
      isPartnerBlocked: newBlockedState,
      blockedBy: newBlockedState ? user?._id : null
    }, togglePartnerBlock).then((result) => {
      if (result.success) {
        toast.success(result.message);
        fetchAnalytics(page, search, filter);
      } else {
        toast.error(result.message || `Failed to ${action} partner`);
      }
    }).catch(() => {
      toast.error(`Failed to ${action} partner`);
    });
  };

  const fetchAnalytics = useCallback((currentPage, currentSearch, currentFilter) => {
    setLoading(true);
    HitApi({
      page: currentPage || page,
      limit: 20,
      search: currentSearch !== undefined ? currentSearch : search,
      filter: currentFilter || filter
    }, getPartnerAnalytics).then((result) => {
      if (result.success) {
        setAnalytics({
          totalPartners: result.data.totalPartners,
          onlinePartners: result.data.onlinePartners,
          offlinePartners: result.data.offlinePartners
        });
        setPartners(result.data.partners);
      }
    }).catch((error) => {
      console.error('Error fetching partner analytics:', error);
    }).finally(() => {
      setLoading(false);
    });
  }, [page, search, filter]);

  useEffect(() => {
    fetchAnalytics(page, search, filter);
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    const seconds = refreshInterval / 1000;
    setCountdown(seconds);

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? seconds : prev - 1));
    }, 1000);

    intervalRef.current = setInterval(() => {
      fetchAnalytics(page, search, filter);
      setCountdown(seconds);
    }, refreshInterval);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countdownRef.current);
    };
  }, [refreshInterval, fetchAnalytics, page, search, filter]);

  const handleSearch = (value) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchAnalytics(1, value, filter);
    }, 400);
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    setPage(1);
    fetchAnalytics(1, search, value);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchAnalytics(newPage, search, filter);
  };

  const cards = [
    {
      title: 'Total Registered Partners',
      value: analytics.totalPartners,
      icon: Users,
      lightColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      title: 'Online Partners',
      value: analytics.onlinePartners,
      icon: UserCheck,
      lightColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      title: 'Offline Partners',
      value: analytics.offlinePartners,
      icon: UserX,
      lightColor: 'bg-red-50',
      textColor: 'text-red-600'
    }
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Overview of partner registration and online status</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-500">Next refresh in {countdown}s</span>
          </div>
          <div className="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden">
            {REFRESH_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setRefreshInterval(opt.value)}
                className={`px-3 py-2 text-xs font-medium transition-colors ${
                  refreshInterval === opt.value
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => { fetchAnalytics(page, search, filter); setCountdown(refreshInterval / 1000); }}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {cards.map((card) => (
          <div
            key={card.title}
            className="bg-white rounded-xl shadow border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {loading ? '—' : card.value}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${card.lightColor}`}>
                <card.icon className={`w-6 h-6 ${card.textColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Partners Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100">
        {/* Table Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Partners</h2>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name, phone, email..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg w-72 focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
            </div>
            <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden">
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleFilterChange(opt.value)}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    filter === opt.value
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Unit</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Registered</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Blocked</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {partners.docs && partners.docs.length > 0 ? (
                partners.docs.map((partner, index) => (
                  <tr key={partner._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {((partners.page || page) - 1) * 20 + index + 1}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {partner.profilePic ? (
                          <img src={partner.profilePic} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {partner.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-900">{partner.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{partner.phoneNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{partner.email || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {partner.unitIds && partner.unitIds.length > 0
                        ? partner.unitIds.map(u => u.unitName || u.unitCode).join(', ')
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        partner.isOnline
                          ? 'bg-green-50 text-green-700'
                          : 'bg-red-50 text-red-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          partner.isOnline ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        {partner.isOnline ? 'Online' : 'Offline'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {partner.createdAt
                        ? new Date(partner.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {partner.isPartnerBlocked ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                          <ShieldBan className="w-3 h-3" />
                          Blocked
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-500">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditPartner(partner)}
                          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit Partner"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleBlock(partner)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            partner.isPartnerBlocked
                              ? 'text-green-600 hover:text-green-800 hover:bg-green-50'
                              : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                          }`}
                          title={partner.isPartnerBlocked ? 'Unblock Partner' : 'Block Partner'}
                        >
                          {partner.isPartnerBlocked ? <ShieldCheck className="w-4 h-4" /> : <ShieldBan className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-sm text-gray-400">
                    {loading ? 'Loading partners...' : 'No partners found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {partners.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Page {partners.page || page} of {partners.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= partners.totalPages}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Partner Modal */}
      {editPartner && (
        <EditPartnerModal
          partner={editPartner}
          onClose={() => setEditPartner(null)}
          onSaved={() => {
            setEditPartner(null);
            fetchAnalytics(page, search, filter);
          }}
        />
      )}
    </div>
  );
};

export default PartnerAnalytics;
